import { rollbarError } from "../rollbarErrors";
import { ConsentSettings } from "../consentSettings";
import {
  ContentInteraction,
  ABComponentTests,
  LivepersonData,
  CruiseDrawerData
} from "../interfaces";
import {
  AnalyticsProvider,
  ConversionEventDetail
} from "./analyticsProviderBase";

abstract class Google extends AnalyticsProvider {
  async get_visitor_id(account_id?: string): Promise<unknown> {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.performance == true;

    const timeout = new Promise(function (resolve, reject) {
      setTimeout(function () {
        reject("Google ID could not be retrieved.");
      }, 4000);
    });

    const p = new Promise((resolve, reject) => {
      if (!this.has_consent_to_run) {
        this.log("Consent not given to retrieve GA ID.");
        reject("Consent not given to retrieve GA ID.");
      } else {
        gtag(
          "get",
          account_id ? account_id : "G-ZXCJY21NXY",
          "client_id",
          (client_id) => {
            if (typeof client_id == "string") {
              resolve(client_id);
            } else {
              reject("Unexpected type for Google ID: " + typeof client_id);
            }
          }
        );
      }
    });

    return Promise.race([p, timeout]).catch(function (e) {
      this.log(e);
      return null;
    });
  }
}

export class GoogleAnalytics extends Google {
  pageloads_complete = 0;
  product_impressions = new Set([]);
  page_list_name = "unknown";

  constructor() {
    super();
    this.name = "GoogleAnalytics";
  }

  split_custom_param(param_name, param): Record<string, string> {
    function chunk(str, sz) {
      const new_str = [];
      let st = 0;
      let sb;
      do {
        sb = str.substr(st, sz);
        if (sb) {
          new_str.push(sb);
          st += sz;
        }
      } while (sb);
      return new_str;
    }

    const chunked = chunk(param, 100);
    const params_chunked = {};
    params_chunked[`${param_name}_0`] = "";
    for (let i = 0; i < Math.min(chunked.length, 10); i++) {
      params_chunked[`${param_name}_${i}`] = chunked[i];
    }
    return params_chunked;
  }

  set_page_custom_dimensions(): void {
    this.log("Setting up GA3 and GA4 config");

    const vd = sv.get_voyage_details();
    const dd = sv.get_destination_details();

    const currency = vd.voyage_price_currency || vd.lead_value_currency;

    // GA4

    const debug_mode = window.ss_debug_mode || false;

    const ga4_config = {
      ...{
        transport_type: "beacon",
        send_page_view: false,
        user_id: sv.get_auid(),
        page_type: sv.get_page_type(),
        page_cat1: sv.get_page_cat1(),
        page_cat2: sv.get_page_cat2(),
        // platform_market: sv.get_platform_market(), TO USE WHEN NEW GA4 PROPERTY STARTED
        platform: sv.get_platform_market(),
        user_country: sv.get_user_country(),
        user_language: sv.get_user_language(),
        destination_id: dd.destination_id,
        destination_name: dd.name,
        destination_type: dd.destination_type,
        // voyage_adults_number: vd.adults_number,  TO USE WHEN NEW GA4 PROPERTY STARTED
        voyage_details_adults_number: vd.adults_number,
        // voyage_flight_option: vd.flight_option, TO USE WHEN NEW GA4 PROPERTY STARTED
        voyage_details_flight_option: vd.flight_option,
        // voyage_kids_number: vd.kids_number, TO USE WHEN NEW GA4 PROPERTY STARTED
        voyage_details_kids_number: vd.kids_number,
        voyage_lead_value: vd.lead_value,
        // voyage_lead_value_currency: vd.lead_value_currency, TO USE WHEN NEW GA4 PROPERTY STARTED
        voyage_details_lead_value_currency: vd.lead_value_currency,
        // voyage_name: vd.name, TO USE WHEN NEW GA4 PROPERTY STARTED
        voyage_details_name: vd.name,
        voyage_details_cruise_type: vd.cruise_type,
        voyage_details_suite_is_auto_selection: vd.is_automatic_selection,
        voyage_arrival_date: vd.arrival_date,
        voyage_arrival_port_code: vd.arrival_port,
        voyage_code: vd.code,
        voyage_days_duration: vd.days_duration,
        voyage_departure_date: vd.departure_date,
        voyage_departure_port_code: vd.departure_port,
        voyage_price: vd.price,
        voyage_ship_name: vd.ship_name,
        voyage_suite: vd.suite,
        voyage_type: vd.type,
        voyage_funnel_step: vd.conversion_step,
        voyage_funnel_step_name: vd.step_name,
        voyage_destination_id: vd.destination_id,
        voyage_destination_name: vd.destination_name,
        experiment_layout_status: sv.get_experiment_layout_status(),
        experiment_id: sv.get_experiment_id(),
        experiment_segment_id: sv.get_experiment_segment_id(),
        // not clear if these below will be needed
        // conversionPath: '',
        // AUID: '',
        // UWRID: '',
        // WRID: '',
        // 'DataLayer api indiv id': '',
        // request_brochure: '',
        raw_referrer: document.referrer,
        // conversion_path_page_type: ''
        raw_url: window.location.href,
        version: sv.get_version(),
        currency: currency,
        my_silversea_usertype: sv.get_my_silversea_vals()?.usertype,
        my_silversea_env: sv.get_my_silversea_vals()?.env,
        my_silversea_individual_id: sv.get_my_silversea_vals()?.individual_id,
        content_group: sv.get_page_type(),
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        ab_component_tests: JSON.stringify(
          this.get_ab_test_component_experiments()
        )
      },
      ...this.split_custom_param("raw_url", window.location.href),
      ...this.split_custom_param("raw_referrer", document.referrer)
    };

    if (debug_mode == true) {
      this.log("DEBUG MODE DETECTED FOR GA4...");
      ga4_config["debug_mode"] = true;
    }

    gtag("config", "G-ZXCJY21NXY", ga4_config);
    this.log("GA4 initiated...");

    // determine the friendly list name for this page...
    this.page_list_name =
      sv.get_page_cat1_friendly() + "-" + sv.get_page_cat2_friendly() + "-list";
    // Removed because Universal Analytics has been deprecated
    // GA3
    // gtag("config", "UA-5494850-14", {
    //   transport_type: "beacon",
    //   send_page_view: false,
    //   currency: currency,
    //   custom_map: {
    //     dimension1: "user_country",
    //     dimension2: "platform_market",
    //     dimension3: "user_language",
    //     dimension4: "page_type",
    //     dimension5: "page_path",
    //     dimension6: "conversion_path",
    //     dimension7: "auid",
    //     dimension8: "uwrid",
    //     dimension9: "wrid",
    //     dimension10: "api_indiv_id",
    //     dimension11: "voyage_departure_port",
    //     dimension12: "voyage_arrival_port",
    //     dimension13: "voyage_departure_date",
    //     dimension14: "voyage_name",
    //     dimension15: "voyage_days_duration",
    //     dimension16: "voyage_code",
    //     dimension17: "voyage_suite",
    //     // 'dimension18': , (not used)
    //     dimension19: "conversion_path_page_type",
    //     dimension20: "brochure_name",
    //     dimension21: "page_cat2",
    //     dimension22: "voyage_ship_name",
    //     dimension23: "voyage_adults_number",
    //     dimension24: "voyage_kids_number",
    //     dimension25: "voyage_flight_option",
    //     dimension26: "voyage_conversion_step",
    //     dimension27: "voyage_lead_value",
    //     dimension28: "voyage_lead_value_currency",
    //     dimension29: "referrer",
    //     dimension30: "page_cat1",
    //     dimension31: "raw_url",
    //     dimension32: "version",
    //     dimension33: "my_silversea_usertype",
    //     dimension34: "my_silversea_env",
    //     dimension35: "my_silversea_individual_id",
    //     dimension36: "experiment_id",
    //     dimension37: "experiment_segment_id",
    //     dimension38: "viewport_width",
    //     dimension39: "viewport_height"
    //   },

    //   user_country: sv.get_user_country(),
    //   platform_market: sv.get_platform_market(),
    //   user_language: sv.get_user_language(),
    //   page_type: sv.get_page_type(),
    //   page_path: sv.get_page_path(),
    //   conversion_path: sv.get_cookie("conversion_path"),
    //   auid: sv.get_auid(),
    //   // 'dimension8': {{UWRID}}, known on web request
    //   // 'dimension9': {{WRID}},
    //   api_indiv_id: sv.search_datalayer(["api_indiv_id"]),
    //   voyage_departure_port: vd.departure_port,
    //   voyage_arrival_port: vd.arrival_port,
    //   voyage_departure_date: vd.departure_date,
    //   voyage_name: vd.name,
    //   voyage_days_duration: vd.days_duration,
    //   voyage_code: vd.code,
    //   voyage_suite: vd.suite,
    //   // 'dimension18': , (not used)
    //   conversion_path_page_type: sv.get_cookie("conversion_path_page_type"),
    //   // 'dimension20': {{request_brochure}}, known on web request
    //   page_cat2: sv.get_page_cat2(),
    //   voyage_ship_name: vd.ship_name,
    //   voyage_adults_number: vd.adults_number,
    //   voyage_kids_number: vd.kids_number,
    //   voyage_flight_option: vd.flight_option,
    //   voyage_conversion_step: vd.conversion_step,
    //   voyage_lead_value: vd.lead_value,
    //   voyage_lead_value_currency: vd.lead_value_currency,
    //   referrer: document.referrer,
    //   page_cat1: sv.get_page_cat1(),
    //   raw_url: window.location.href,
    //   version: sv.get_version(),
    //   my_silversea_usertype: sv.get_my_silversea_vals()?.usertype,
    //   my_silversea_env: sv.get_my_silversea_vals()?.env,
    //   my_silversea_individual_id: sv.get_my_silversea_vals()?.individual_id,

    //   experiment_id: sv.get_experiment_id(),
    //   experiment_segment_id: sv.get_experiment_segment_id(),

    //   viewport_width: window.innerWidth,
    //   viewport_height: window.innerHeight,

    //   content_group1: sv.get_page_type(),

    //   // 'allow_google_signals': false,
    //   // 'allow_ad_personalization_signals': false,
    //   linker: {
    //     accept_incoming: true,
    //     domains: ["event.webinarjam.com"]
    //   }
    // });

    // this.log("GA3 initiated...");
  }

  start(_consent_settings: ConsentSettings): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.performance == true;

    if (!this.has_consent_to_run) {
      this.has_started = true;
      return;
    } else if (this.has_started) {
      return;
    }

    // this.trigger_event("ga-starting");

    if (document.domain == "127.0.0.1") {
      this.log(
        "Skipping inserting the GA4 tag because you are running locally."
      );
    } else {
      if (this.has_consent_to_run) {
        if (!this.has_started && this.pageloads_complete == 0) {
          // This triggers a GA3 pageview >:-( so have to deal with
          // that in the pageview logic...

          this.log("Adding GA4 Tag...");
          const d = document,
            g = d.createElement("script"),
            s = d.getElementsByTagName("script")[0];
          g.type = "text/javascript";
          g.async = true;
          g.defer = false;
          g.src = "https://www.googletagmanager.com/gtag/js?id=G-ZXCJY21NXY";
          g.onload = () => {
            window.dataLayer = window.dataLayer || [];
            gtag("set", "linker", { domains: ["silversea.com"] });
            gtag("js", new Date());

            function bool_to_text(bool) {
              return bool ? "granted" : "denied";
            }

            const gtagConsentConfig = {
              ad_storage: bool_to_text(sv.onetrust_consent.targeting),
              analytics_storage: bool_to_text(sv.onetrust_consent.essential),
              ad_personalization: bool_to_text(sv.onetrust_consent.targeting),
              ad_user_data: bool_to_text(sv.onetrust_consent.targeting),
              wait_for_update: 500
            };

            gtag("consent", "default", gtagConsentConfig);

            this.set_page_custom_dimensions();

            // initialise all other scripts depending on gtag
            wspr.S("gtag_init", "gtag_initialisation", (wspr_event) => {
              const message = wspr_event.m;
              gtag(...message);
              return true;
            });

            // set as has started
            this.has_started = true;

            // process all non initialisation gtags
            wspr.S("gtag", "trigger_gtag", (wspr_event) => {
              const message = wspr_event.m;
              gtag(...message);
              return true;
            });

            wspr.S(
              "ab-component-test-shown",
              `analytics_provider_${this.name}`,
              async (wspr_event) => {
                this.ab_component_test_shown(wspr_event.m);
                return true;
              }
            );

            return true;
          };
          s.parentNode.insertBefore(g, s);
        }
      } else {
        this.log("GA does not have consent to run.");
      }
    }

    return;
  }

  liveperson_load(_liveperson_data: LivepersonData): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.performance == true;
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_liveperson_load();
    }
    wspr.P("gtag", [
      "event",
      "liveperson_load",
      {
        send_to: "G-ZXCJY21NXY",
        liveperson_user_id: _liveperson_data.vid,
        liveperson_session_id: _liveperson_data.sid
      }
    ]);
  }

  scroll_triggered(_scroll_perc: number): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.performance == true;
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_scroll();
    }
    wspr.P("gtag", [
      "event",
      "scroll",
      {
        send_to: "G-ZXCJY21NXY",
        percent_scrolled: _scroll_perc
      }
    ]);
  }

  conversion_triggered(_conversion_event_detail: ConversionEventDetail): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.performance == true;
    if (!this.has_consent_to_run) {
      this.log_conversion_not_allowed();
      return;
    } else {
      this.log_conversion();
    }

    if (_conversion_event_detail.request_type != "TYP") {
      const vd = sv.get_voyage_details();

      if (_conversion_event_detail.request_type == "WBBK") {
        // Removed because Universal Analytics has been deprecated
        // wspr.P("gtag", [
        //   "event",
        //   "purchase",
        //   {
        //     send_to: "UA-5494850-14",
        //     transaction_id: _conversion_event_detail.purchase.transaction_id,
        //     value: _conversion_event_detail.purchase.revenue,
        //     items: [this.map_to_ga3_item(this.get_enhanced_commerce_product())]
        //   }
        // ]);

        wspr.P("gtag", [
          "event",
          "purchase",
          {
            send_to: "G-ZXCJY21NXY",
            transaction_id: _conversion_event_detail.purchase.transaction_id,
            value: _conversion_event_detail.purchase.revenue,
            items: [this.get_enhanced_commerce_product()]
          }
        ]);

        this.debug("Enhanced Ecommerce: Purchase");
      }

      // GA4
      wspr.P("gtag", [
        "event",
        "generate_lead",
        {
          send_to: "G-ZXCJY21NXY",
          request_type: _conversion_event_detail.request_type,
          is_agent: _conversion_event_detail.is_agent,
          brochure: _conversion_event_detail.brochure,
          currency: vd.lead_value_currency,
          value: vd.lead_value,
          uwrid: _conversion_event_detail.uwrid,
          form_name: _conversion_event_detail.formName,
          phone_number_is_present: _conversion_event_detail.phone_number_is_present,
          email: _conversion_event_detail.email,
          phone_number: _conversion_event_detail.phone_number,
          first_name: _conversion_event_detail.first_name,
          last_name: _conversion_event_detail.last_name,
        }
      ]);
      // Removed because Universal Analytics has been deprecated
      // wspr.P("gtag", [
      //   "event",
      //   _conversion_event_detail.request_type,
      //   {
      //     send_to: "UA-5494850-14",
      //     event_category: "form_submit",
      //     event_label: sv.get_user_language(),
      //     uwrid: _conversion_event_detail.uwrid,
      //     wrid: _conversion_event_detail.wrid,
      //     brochure_name: _conversion_event_detail.brochure
      //   }
      // ]);
    } else {
      this.log("Google Analytics Conversion Skipped due to being TYP");
    }
  }

  ab_component_test_shown(_ab_component_test: ABComponentTests): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.performance == true;
    if (!this.has_consent_to_run) {
      return;
    } else {
      // this.log_interaction();
    }

    // GA4
    this.log("starting ga4 ab component test shown...");
    wspr.P("gtag", [
      "event",
      "ab_conponent_test_shown",
      {
        send_to: "G-ZXCJY21NXY",
        ab_component_tests: JSON.stringify(_ab_component_test)
      }
    ]);

    this.log("completed ga4 ab component test shown...");
  }

  cruise_drawer_opened(_cruise_drawer_data: CruiseDrawerData): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.performance == true;
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_cruise_drawer_opened();
    }
  
    const details = _cruise_drawer_data.voyage_details;
  
    wspr.P("gtag", [
      "event",
      "cruise_drawer_opened",
      {
        send_to: "G-ZXCJY21NXY",
        arrival_date: details.arrival_date,
        arrival_port: details.arrival_port,
        code: details.code,
        cruise_type: details.cruise_type,
        days_duration: details.days_duration,
        departure_date: details.departure_date,
        departure_port: details.departure_port,
        destination_id: details.destination_id,
        destination_name: details.destination_name,
        name: details.name,
        price: details.price,
        sale_state: details.sale_state,
        ship_name: details.ship_name
      }
    ]);

    this.log("Cruise drawer event to GA4 sent");
  }

  interaction_triggered(_data_content_attribs: ContentInteraction): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.performance == true;
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_interaction();
    }

    // GA4 Interaction Event
    // cannot use click for event name because it is a reserved event name in GA4.
    const event_name =
      _data_content_attribs.event_type == "click"
        ? "content_click"
        : _data_content_attribs.event_type;

    // GA4
    this.log("starting ga4 event...");
    wspr.P("gtag", [
      "event",
      event_name,
      {
        send_to: "G-ZXCJY21NXY",
        event_action_type: _data_content_attribs.event_type,
        data_content_name: _data_content_attribs["data-content-name"],
        data_content_piece: _data_content_attribs["data-content-piece"],
        data_content_target: _data_content_attribs["data-content-target"],
        data_content_value: _data_content_attribs["data-content-value"],
        data_content_params: JSON.stringify(
          _data_content_attribs["data-content-params"]
        ),
        data_content_element_type: [
          "page_cta_button",
          "request_a_quote",
          "view_the_brochures",
          "suite-select",
          "details",
          "apply",
          "next-button",
          "submit_button",
          "book_your_cruise"
        ].includes(_data_content_attribs["data-content-piece"])
          ? "CTA"
          : undefined,
        data_content_ab_component_test: JSON.stringify(
          _data_content_attribs["data-content-ab-component-test"]
        )
      }
    ]);

    this.log("completed ga4 event...");
    // Removed because Universal Analytics has been deprecated
    // GA3
    // const g3_event_action = [
    //   "page_cta_button",
    //   "request_a_quote",
    //   "view_the_brochures",
    //   "suite-select",
    //   "details",
    //   "apply",
    //   "next-button",
    //   "submit_button"
    // ].includes(_data_content_attribs["data-content-piece"])
    //   ? "CTA"
    //   : _data_content_attribs["data-content-piece"];

    // const g3_event_label =
    //   _data_content_attribs["data-content-name"] +
    //   " :: " +
    //   _data_content_attribs["data-content-piece"] +
    //   " :: " +
    //   _data_content_attribs["data-content-value"] +
    //   " :: " +
    //   _data_content_attribs["data-content-target"];

    const cp = _data_content_attribs["data-content-piece"];
    if (["cruise_details", "book_your_cruise"].indexOf(cp) >= 0) {
      this.log("processing cruise details click...");
      // NEED TO GET THESE FROM data-content-params
      const dcp = _data_content_attribs["data-content-params"];
      this.log(dcp);
      const product_details =
        dcp !== null
          ? {
              item_id: dcp.voyageCode,
              // item_name: vd.name,
              item_brand: dcp.shipName,
              item_category: dcp.destinationName,
              item_list_name: this.page_list_name,
              // variant: vd.selected_fare,
              // list_position: 1,
              quantity: 1,
              price: dcp.priceFrom
              // coupon: vd.isVsMember ? "Venetian Society" : undefined
            }
          : this.get_enhanced_commerce_product();

      const cp_evnt_type = {
        cruise_details: "select_item",
        book_your_cruise: "add_to_cart"
      };
      const evnt_type = cp_evnt_type[cp];

      if (evnt_type) {
        // Removed because Universal Analytics has been deprecated
        // wspr.P("gtag", [
        //   "event",
        //   evnt_type,
        //   {
        //     send_to: "UA-5494850-14",
        //     items: [this.map_to_ga3_item(product_details)]
        //   }
        // ]);
        wspr.P("gtag", [
          "event",
          evnt_type,
          {
            send_to: "G-ZXCJY21NXY",
            items: [product_details]
          }
        ]);
        this.debug("Enhanced Ecommerce: " + evnt_type);
      }
    }
    // Removed because Universal Analytics has been deprecated
    // wspr.P("gtag", [
    //   "event",
    //   g3_event_action,
    //   {
    //     send_to: "UA-5494850-14",
    //     event_category: _data_content_attribs["data-content-name"],
    //     event_label:
    //       _data_content_attribs["data-content-name"] +
    //       " :: " +
    //       _data_content_attribs["data-content-piece"] +
    //       " :: " +
    //       _data_content_attribs["data-content-value"] +
    //       " :: " +
    //       _data_content_attribs["data-content-target"]
    //   }
    // ]);
  }

  get_enhanced_commerce_product(): {
    item_id: string | number | boolean;
    item_name: string | number | boolean;
    item_brand: string | number | boolean;
    item_category: string | number | boolean;
    item_list_name: string;
    variant: string | number | boolean;
    // list_position: number;
    quantity: string | number | boolean;
    price: string | number | boolean;
    coupon?: string | number | boolean;
  } {
    const vd = sv.get_voyage_details();
    if (vd && Object.keys(vd).length !== 0) {
      return {
        item_id: vd.code,
        item_name: vd.name,
        item_brand: vd.ship_name,
        item_category: vd.destination_name,
        item_list_name: this.page_list_name,
        variant: vd.selected_fare,
        // list_position: 1,
        quantity: 1,
        price: vd.lead_value || vd.price,
        coupon: vd.isVsMember ? "Venetian Society" : undefined
      };

      this.debug("Enhanced Ecommerce: get product");
    }
  }

  map_to_ga3_item(ga4_item: {
    item_id: string | number | boolean;
    item_name?: string | number | boolean;
    item_brand?: string | number | boolean;
    item_category?: string | number | boolean;
    item_list_name: string;
    variant?: string | number | boolean;
    // list_position: number;
    quantity: string | number | boolean;
    price?: string | number | boolean;
    coupon?: string | number | boolean;
  }): Record<string, unknown> {
    const ga3_item = {};

    for (const [key, value] of Object.entries(ga4_item)) {
      ga3_item[key.startsWith("item_") ? key.split("item_")[1] : key] = value;
    }
    return ga3_item;
  }

  scan_for_product_impressions(): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.performance == true;
    if (this.has_started && this.has_consent_to_run) {
      const cs = new Set(
        document.querySelectorAll('[data-content-piece="cruise_details"]')
      );

      const imps = [];

      const num_of_product_impressions = this.product_impressions.size;
      let i = 1;
      cs.forEach((cruise_element) => {
        const dcp_str = cruise_element.getAttribute("data-content-params");
        if (!this.product_impressions.has(dcp_str)) {
          const dcp = JSON.parse(dcp_str);

          this.product_impressions.add(dcp_str);
          imps.push({
            item_id: dcp.voyageCode,
            // name: vd.name,
            item_category: dcp.destinationName,
            item_brand: dcp.shipName,
            item_list_name: this.page_list_name,
            // variant: vd.flight_option,
            price: dcp.priceFrom,
            quantity: 1,
            position: i
          });
        }
        i += 1;
      });

      if (num_of_product_impressions < this.product_impressions.size) {
        // Removed because Universal Analytics has been deprecated
        // wspr.P("gtag", [
        //   "event",
        //   "view_item_list",
        //   {
        //     send_to: "UA-5494850-14",
        //     item_list_id: this.page_list_name,
        //     item_list_name: this.page_list_name,
        //     items: imps.map(this.map_to_ga3_item)
        //   }
        // ]);

        wspr.P("gtag", [
          "event",
          "view_item_list",
          {
            send_to: "G-ZXCJY21NXY",
            item_list_id: this.page_list_name,
            item_list_name: this.page_list_name,
            items: imps
          }
        ]);

        this.debug(
          `Enhanced Ecommerce: addImpression run for ${
            this.product_impressions.size - num_of_product_impressions
          } new products`
        );
      }
    }
  }

  pageload_triggered(): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.targeting == true;
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_pageload();
    }

    this.set_page_custom_dimensions();
    this.initialise_subscribers();

    if (!sv.get_page_cat1()) {
      rollbarError("PAGE_CAT1 EMPTY", new Error(JSON.stringify(wspr)));
    }

    wspr.P("gtag", [
      "event",
      "page_view",
      {
        // page_title: '<Page Title>',
        // page_location: '<Page Location>',
        // page_path: '<Page Path>',
        send_to: "G-ZXCJY21NXY",
        non_interaction: false
      }
    ]);

    // GA3
    const vd = sv.get_voyage_details();
    this.debug("Enhanced Ecommerce: SETUP");

    // this.enhanced_commerce_add_product();

    this.product_impressions = new Set([]);
    this.scan_for_product_impressions();

    // ecommerce
    if (vd && Object.keys(vd).length !== 0) {
      if (sv.get_page_cat1() == "ecommerce") {
        const pr = this.get_enhanced_commerce_product();
        // Removed because Universal Analytics has been deprecated
        // wspr.P("gtag", [
        //   "event",
        //   vd.conversion_step == 2 && this.get_is_new_subdomain()
        //     ? "begin_checkout"
        //     : "checkout_progress",
        //   {
        //     send_to: "UA-5494850-14",
        //     items: [this.map_to_ga3_item(this.get_enhanced_commerce_product())],
        //     coupon: pr?.coupon,
        //     checkout_step: vd.conversion_step,
        //     value: pr?.price
        //   }
        // ]);

        wspr.P("gtag", [
          "event",
          vd.conversion_step == 2 && this.get_is_new_subdomain()
            ? "begin_checkout"
            : "checkout_progress",
          {
            send_to: "G-ZXCJY21NXY",
            items: [this.get_enhanced_commerce_product()],
            coupon: pr?.coupon,
            checkout_step: vd.conversion_step,
            value: pr?.price
          }
        ]);

        this.debug("Enhanced Ecommerce: ACTION CHECKOUT");
      } else {
        // gtag DONE
        // ga("ec:setAction", "detail");
        // Removed because Universal Analytics has been deprecated
        // wspr.P("gtag", [
        //   "event",
        //   "view_item",
        //   {
        //     send_to: "UA-5494850-14",
        //     items: [this.map_to_ga3_item(this.get_enhanced_commerce_product())]
        //   }
        // ]);

        wspr.P("gtag", [
          "event",
          "view_item",
          {
            send_to: "G-ZXCJY21NXY",
            items: [this.get_enhanced_commerce_product()]
          }
        ]);

        this.debug("Enhanced Ecommerce: ACTION DETAIL");
      }
    } else {
      this.debug("Enhanced Ecommerce: No VD Detected");
    }
    // Removed because Universal Analytics has been deprecated
    // gtag for GA3
    // wspr.P("gtag", [
    //   "event",
    //   "page_view",
    //   {
    //     // page_title: '<Page Title>',
    //     page_location: window.location.href,
    //     // page_path: '<Page Path>',
    //     send_to: "UA-5494850-14",
    //     non_interaction: false
    //   }
    // ]);
    this.pageloads_complete += 1;

    if (this.is_404_page()) {
      // GA4
      wspr.P("gtag", [
        "event",
        "404",
        {
          event_category: "report",
          event_label: this.get_clean_page(),
          event_value: 1,
          send_to: "G-ZXCJY21NXY",
          non_interaction: true
        }
      ]);
      // Removed because Universal Analytics has been deprecated
      // GTAG for GA3
      // wspr.P("gtag", [
      //   "event",
      //   "404",
      //   {
      //     event_category: "Report",
      //     event_label: this.get_clean_page(),
      //     event_value: 1,
      //     send_to: "UA-5494850-14",
      //     non_interaction: true
      //   }
      // ]);
    }
  }
}

/*
export class GoogleAds extends Google {
  constructor() {
    super();
    this.name = "GoogleAds";
  }

  acl_lookup = {
    US: [
      {
        // "conversion_id": "1014943127",
        // "RAQ": "XXW_CPmImQQQl5v74wM",
        // "RAC": "4ekHCIGahAQQl5v74wM",
        // "RAB": "kakUCIDVllcQl5v74wM",
        // "SFO": "kdt4CPGbhAQQl5v74wM",
        // "ETY": "tgx1COGdhAQQl5v74wM"
      },
      {
        // "conversion_id": "467836898",
        // "pageview": "U72aCN3NovIBEOK_it8B",
        // "RAQ": "Zd_eCKqBgvIBEOK_it8B",
        // // "RAC": "4ekHCIGahAQQl5v74wM",
        // "RAB": "ugzACOHRovIBEOK_it8B",
        // "SFO": "YajhCNiokvIBEOK_it8B"
        // // "ETY": "tgx1COGdhAQQl5v74wM"
      },
      {
        // "conversion_id": '11310738772',
        // "pageview": '2wnUCIXi1a4ZENTasJEq'
      }
    ],
    LAM: [
      {
        // "conversion_id": "985293017",
        // "RAQ": "2ro7CO-klggQ2cHp1QM",
        // "RAC": "LUfyCNenlggQ2cHp1QM",
        // "RAB": "eYA8CO-pllcQ2cHp1QM",
        // "SFO": "LKy0CP-ilggQ2cHp1QM",
        // "ETY": "-YN5COellggQ2cHp1QM"
      }
    ],
    AP: [
      {
        // "conversion_id": "974446676",
        // "RAQ": "7HNzCNzvkQgQ1MDT0AM",
        // "RAC": "OW6VCMTykQgQ1MDT0AM",
        // "RAB": "GiVYCNv4lVcQ1MDT0AM",
        // "SFO": "htQZCOztkQgQ1MDT0AM",
        // "ETY": "8OO5CMzxkQgQ1MDT0AM"
      }
    ],
    UK: [
      {
        // "conversion_id": "958324608",
        // "RAQ": "htdlCLDd2iMQgL_7yAM",
        // "RAC": "EcqGCIjAxSEQgL_7yAM",
        // "RAB": "f4dGCLz6lVcQgL_7yAM",
        // "SFO": "CkrCCPjBxSEQgL_7yAM",
        // "ETY": "80vfCIDBxSEQgL_7yAM",
        // "RAQ": "DV_yCPG-q_IZEIC_-8gD",
        // "RAB": "d-VfCKLAqPIZEIC_-8gD",
        // "SFO": "m_lHCPrjt_IZEIC_-8gD",
        // "WEBQ": "f4hJCK-luPIZEIC_-8gD",
        // "WBBK": "pfNNCKy4qPIZEIC_-8gD",
        // "WBOF.01": "SZLPCJ_yqvIZEIC_-8gD",
        // "WBOF.02": "rNIYCLT7qvIZEIC_-8gD",
        // "WBOF.03": "afqGCP6Aq_IZEIC_-8gD",
        // "WBOF.04": "y9GOCKyluPIZEIC_-8gD"
      }
    ],
    EMEA: [
      {
        // "conversion_id": "974176588",
        // "RAQ": "81HGCJzThggQzILD0AM",
        // "RAC": "Fl7fCPTXhggQzILD0AM",
        // "RAB": "Pr99CPn0llcQzILD0AM",
        // "SFO": "Id1aCKzRhggQzILD0AM",
        // "ETY": "We5mCITWhggQzILD0AM"
      }
    ]
  };

  start(_consent_settings: ConsentSettings): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.targeting == true;
  
    if (!this.has_consent_to_run || this.has_started) {
      this.has_started = true;
      return;
    }
  
    const platform_market = this.get_platform_market();
    const acl = this.acl_lookup[platform_market];
  
    if (
      !platform_market ||
      platform_market === "Failed" ||
      platform_market === "undefined" ||
      typeof platform_market === "undefined"
    ) {
      console.error(
        `GoogleAds Failure: invalid market "${platform_market}", outside analytics control`
      );
    } else if (
      Array.isArray(acl) &&
      acl.length > 0 &&
      acl.some((entry) => entry.conversion_id)
    ) {
      for (let i = 0; i < acl.length; i++) {
        const conversionId = acl[i]?.conversion_id;
        if (!conversionId) continue;
  
        wspr.P("gtag_init", [
          "config",
          "AW-" + conversionId,
          {
            transport_type: "beacon",
            send_page_view: false
          }
        ]);
      }
    } else {
      rollbarError("UNEXPECTED or EMPTY ACL: market = " + platform_market);
    }
  
    this.has_started = true;
    this.log("Google Ads Initiated...");
  }  

  pageload_triggered(): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.targeting == true;
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_pageload();
    }
    this.initialise_subscribers();

    if (document.domain == "127.0.0.1") {
      this.log(
        "Skipping inserting the GA4 tag because you are running locally."
      );
    } else {
      const platform_market = this.get_platform_market();
      const acl = this.acl_lookup[platform_market];
      if (acl) {
        for (let i = 0; i < acl.length; i++) {
          if (acl[i]["pageview"]) {
            wspr.P("gtag", [
              "event",
              "conversion",
              {
                send_to:
                  "AW-" + acl[i].conversion_id + "/" + acl[i]["pageview"],
                value: 1.0,
                currency: "USD"
              }
            ]);
          }
        }
      } else if (
        platform_market == "Failed" ||
        platform_market == "undefined" ||
        typeof platform_market == "undefined"
      ) {
        console.error(
          'GoogleAds Failure due to market found being set to = "' +
            platform_market +
            '", this is outside of analytics control...'
        );
      } else {
        rollbarError(
          "UNEXPECTED MARKET: market = " + platform_market,
          undefined
        );
      }
    }
  }

  conversion_triggered(_conversion_event_detail: ConversionEventDetail): void {
    // force ture to execute the Google tags in Consent Mode
    this.has_consent_to_run = true; // _consent_settings.targeting == true;
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_conversion();
    }

    this.log("Google Ads web-request-submitted triggered");
    const platform_market = this.get_platform_market();

    const acl = this.acl_lookup[platform_market];
    if (acl) {
      for (let i = 0; i < acl.length; i++) {
        if (acl[i][_conversion_event_detail.request_type]) {
          wspr.P("gtag", [
            "event",
            "conversion",
            {
              send_to:
                "AW-" +
                acl[i].conversion_id +
                "/" +
                acl[i][_conversion_event_detail.request_type],
              value: 1.0,
              currency: "USD"
            }
          ]);
        } else if (
          _conversion_event_detail.request_type != "TYP" &&
          _conversion_event_detail.request_type != "WEBQ" &&
          _conversion_event_detail.request_type != "WBBK" &&
          _conversion_event_detail.request_type != "WBOF.01" &&
          _conversion_event_detail.request_type != "WBOF.02" &&
          _conversion_event_detail.request_type != "WBOF.03"
        ) {
          rollbarError(
            "UNEXPECTED MARKET OR CONVERSION: market = " +
              platform_market +
              "; request_type = " +
              _conversion_event_detail.request_type,
            undefined
          );
        }
      }
    } else if (
      platform_market == "Failed" ||
      platform_market == "undefined" ||
      typeof platform_market == "undefined"
    ) {
      console.error(
        'GoogleAds Failure due to market found being set to = "' +
          platform_market +
          '", this is outside of analytics control...'
      );
    }
    this.log("Google Ads web-request-submitted success");
  }
}
*/